import React, { useState, useMemo, useRef } from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import Icon from 'components/Icon';
import { usePerformance } from '../context';
import {
  CrmStatus,
  RestaurantCrmData,
  Restaurant,
  UploadedList
} from '../types';
import history from '../../../history';

// ─── Styled ───

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  .search-input {
    flex: 1;
    min-width: 200px;
    padding: 8px 12px;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    font-size: 14px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};

    &:focus {
      outline: none;
      border-color: #e25d34;
    }
  }
`;

const FilterPill = styled.button<{ $active: boolean }>`
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 16px;
  border: 1px solid ${(props) => (props.$active ? '#e25d34' : '#d0d0d0')};
  background: ${(props) => (props.$active ? '#e25d34' : 'white')};
  color: ${(props) => (props.$active ? 'white' : '#2b3d54')};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: #e25d34;
  }
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #f0f4f8;
  border-radius: 4px;
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
  font-size: 13px;
  color: #2b3d54;

  select {
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #d0d0d0;
    font-size: 13px;
  }

  button {
    padding: 5px 16px;
    background: #e25d34;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;

    &:hover {
      background: #c94d2a;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 13px;

    th {
      font-family: ${(props) => props.theme.fonts.oswald};
      text-transform: uppercase;
      font-size: 12px;
      font-weight: 500;
      color: #6c757d;
      text-align: left;
      padding: 10px 12px;
      border-bottom: 2px solid #e8e9eb;
      white-space: nowrap;
      cursor: pointer;
      user-select: none;

      &:hover {
        color: #2b3d54;
      }
    }

    td {
      padding: 10px 12px;
      border-bottom: 1px solid #f0f0f0;
      color: #2b3d54;
    }

    tr.clickable {
      cursor: pointer;
      &:hover td {
        background: #fdf5f3;
      }
    }

    .num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
  }
`;

const StatusBadge = styled.span<{ $status: CrmStatus }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  ${(props) => {
    switch (props.$status) {
      case 'active':
        return 'background: #d4edda; color: #155724;';
      case 'won':
        return 'background: #d4edda; color: #155724;';
      case 'to_win_over':
        return 'background: #fff3cd; color: #856404;';
      case 'lost':
        return 'background: #f8d7da; color: #721c24;';
      case 'prospect':
        return 'background: #d1ecf1; color: #0c5460;';
      case 'inactive':
        return 'background: #e2e3e5; color: #383d41;';
      case 'under_review':
        return 'background: #e8d5f5; color: #6f42c1;';
      default:
        return 'background: #e2e3e5; color: #383d41;';
    }
  }}
`;

const CheckboxCell = styled.td`
  width: 36px;

  input[type='checkbox'] {
    cursor: pointer;
    width: 16px;
    height: 16px;
  }
`;

// ─── Status filter options ───

const STATUS_FILTERS: { value: CrmStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'to_win_over', label: 'To Win Over' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'lost', label: 'Lost' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'under_review', label: 'Under Review' }
];

const formatStatus = (s: CrmStatus) => s.replace(/_/g, ' ');

// ─── Component ───

const RestaurantList = () => {
  const {
    state,
    addActivity,
    addRestaurant,
    addList,
    deleteList,
    getActiveList,
    setFilters
  } = usePerformance();
  const { restaurants, crmData, lists, filters } = state;
  const activeList = getActiveList();

  const [search, setSearch] = useState('');
  const initialStatus = (() => {
    const params = new URLSearchParams(history.location.search);
    const s = params.get('status');
    if (
      s &&
      [
        'active',
        'won',
        'to_win_over',
        'prospect',
        'lost',
        'inactive',
        'under_review'
      ].includes(s)
    ) {
      return s as CrmStatus;
    }
    return 'all' as const;
  })();
  const [statusFilter, setStatusFilter] = useState<CrmStatus | 'all'>(
    initialStatus
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sortField, setSortField] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showManageLists, setShowManageLists] = useState(false);
  const [uploadListName, setUploadListName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string[][] | null>(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newName, setNewName] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newSite, setNewSite] = useState('');

  const handleAddRestaurant = () => {
    if (!newName.trim() || !newCity.trim()) return;
    const stub: Omit<Restaurant, 'store_id'> = {
      is_new_store: true,
      store_type: 'restaurant',
      state: 'other',
      state_translated: 'Under Review',
      is_flow_store: false,
      continuous_mode: false,
      network_code: 'ON',
      name: newName.trim(),
      evolution: '',
      displayed_currency: 'EUR',
      hotel: '',
      group: '',
      latitude: 0,
      longitude: 0,
      photo: null,
      michelin_green_star: false,
      michelin_stars: 0,
      orig_stars: '',
      ws_award: '',
      world_best: null,
      wine_service_rating: null,
      region: '',
      address: newAddress.trim(),
      postal_code: '',
      city: newCity.trim(),
      country: '',
      country_code: '',
      phone: newPhone.trim(),
      email: newEmail.trim(),
      site: newSite.trim(),
      dn_total: 0,
      dn_bdx: 0,
      dn_champ: 0,
      dn_italy: 0,
      dn_napa: 0,
      avg_all_price: null,
      median_all_price: null,
      median_btg_price: null,
      checks_validity_date: '',
      client_bottles_details: null,
      rival_bottles_details: null,
      client_refs_by_wine: null,
      rival_wines: null,
      active_rivals: [],
      client_refs_by_bottle: null
    };
    addRestaurant(stub);
    setShowAddForm(false);
    setNewName('');
    setNewCity('');
    setNewAddress('');
    setNewPhone('');
    setNewEmail('');
    setNewSite('');
  };

  // ── CSV upload helpers ──

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    setUploadError('');
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) {
        setUploadError('CSV must have a header row and at least one data row');
        setUploadPreview(null);
        return;
      }
      const header = lines[0]
        .split(',')
        .map((h) => h.trim().toLowerCase().replace(/"/g, ''));
      if (!header.includes('name') || !header.includes('city')) {
        setUploadError('CSV must contain "name" and "city" columns');
        setUploadPreview(null);
        return;
      }
      const rows = lines
        .slice(1)
        .map((line) =>
          line.split(',').map((cell) => cell.trim().replace(/^"|"$/g, ''))
        );
      setUploadPreview([header, ...rows.slice(0, 5)]);
    };
    reader.readAsText(file);
  };

  const handleUploadSubmit = () => {
    if (!uploadFile || !uploadListName.trim()) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      const header = lines[0]
        .split(',')
        .map((h) => h.trim().toLowerCase().replace(/"/g, ''));
      const nameIdx = header.indexOf('name');
      const cityIdx = header.indexOf('city');
      const addrIdx = header.indexOf('address');
      const phoneIdx = header.indexOf('phone');
      const emailIdx = header.indexOf('email');
      const siteIdx = header.indexOf('website');

      const dataRows = lines
        .slice(1)
        .map((line) =>
          line.split(',').map((cell) => cell.trim().replace(/^"|"$/g, ''))
        );

      const newRestaurants: Restaurant[] = [];
      const storeIds: number[] = [];

      dataRows.forEach((row) => {
        const rName = row[nameIdx] || '';
        const rCity = cityIdx >= 0 ? row[cityIdx] || '' : '';
        if (!rName) return;
        // No matching in demo — all rows create new restaurants
        const tempId =
          -(Date.now() % 1000000) - Math.floor(Math.random() * 10000);
        const restaurant: Restaurant = {
          store_id: tempId,
          is_new_store: true,
          store_type: 'restaurant',
          state: 'other',
          state_translated: 'Under Review',
          is_flow_store: false,
          continuous_mode: false,
          network_code: 'ON',
          name: rName,
          evolution: '',
          displayed_currency: 'EUR',
          hotel: '',
          group: '',
          latitude: 0,
          longitude: 0,
          photo: null,
          michelin_green_star: false,
          michelin_stars: 0,
          orig_stars: '',
          ws_award: '',
          world_best: null,
          wine_service_rating: null,
          region: '',
          address: addrIdx >= 0 ? row[addrIdx] || '' : '',
          postal_code: '',
          city: rCity,
          country: '',
          country_code: '',
          phone: phoneIdx >= 0 ? row[phoneIdx] || '' : '',
          email: emailIdx >= 0 ? row[emailIdx] || '' : '',
          site: siteIdx >= 0 ? row[siteIdx] || '' : '',
          dn_total: 0,
          dn_bdx: 0,
          dn_champ: 0,
          dn_italy: 0,
          dn_napa: 0,
          avg_all_price: null,
          median_all_price: null,
          median_btg_price: null,
          checks_validity_date: '',
          client_bottles_details: null,
          rival_bottles_details: null,
          client_refs_by_wine: null,
          rival_wines: null,
          active_rivals: [],
          client_refs_by_bottle: null
        };
        newRestaurants.push(restaurant);
        storeIds.push(tempId);
      });

      const list: UploadedList = {
        id: `list-${Date.now()}`,
        name: uploadListName.trim(),
        store_ids: storeIds,
        created_at: new Date().toISOString(),
        row_count: dataRows.length
      };

      addList(list, newRestaurants);
      setShowUploadModal(false);
      setUploadListName('');
      setUploadFile(null);
      setUploadPreview(null);
      setUploadError('');
      // Auto-select the new list in filters
      setFilters({ activeListId: list.id });
    };
    reader.readAsText(uploadFile);
  };

  const downloadTemplate = () => {
    const template =
      'name,city,address,phone,email,website\n"Example Restaurant","London","123 Main St","+44 20 1234 5678","info@example.com","https://example.com"\n';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'restaurant_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Build enriched rows
  const rows = useMemo(() => {
    const crmMap = new Map<number, RestaurantCrmData>();
    crmData.forEach((c) => crmMap.set(c.restaurant_id, c));

    return restaurants.map((r) => {
      const crm = crmMap.get(r.store_id);
      return {
        ...r,
        crm_status: crm?.crm_status || ('prospect' as CrmStatus),
        last_contact_date: crm?.last_contact_date || null,
        priority: crm?.priority || 'low',
        shelves: r.client_refs_by_wine
          ? Object.values(r.client_refs_by_wine).reduce((a, b) => a + b, 0)
          : 0,
        won_refs: r.client_bottles_details
          ? r.client_bottles_details.filter((b) => b.evolution === 'won').length
          : 0,
        to_win: r.rival_wines
          ? Object.values(r.rival_wines).reduce((a, b) => a + b, 0)
          : 0
      };
    });
  }, [restaurants, crmData]);

  // Filter
  const filtered = useMemo(() => {
    let list = rows;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.address.toLowerCase().includes(q) ||
          r.city.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter((r) => r.crm_status === statusFilter);
    } else if (!activeList) {
      // Hide "under_review" restaurants unless a list is active or explicitly filtered
      list = list.filter((r) => r.crm_status !== 'under_review');
    }
    // Filter by active uploaded list
    if (activeList) {
      const idSet = new Set(activeList.store_ids);
      list = list.filter((r) => idSet.has(r.store_id));
    }
    return list;
  }, [rows, search, statusFilter, activeList]);

  // Sort
  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let va: any = (a as any)[sortField];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let vb: any = (b as any)[sortField];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, sortField, sortDir]);

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleRow = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === sorted.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(sorted.map((r) => r.store_id)));
    }
  };

  const handleBulkDownload = () => {
    if (selected.size === 0) return;
    const selectedRows = sorted.filter((r) => selected.has(r.store_id));
    const header = [
      'Name',
      'City',
      'Address',
      'Stars',
      'Status',
      'Refs',
      'Won',
      'Rivals',
      'Last Contact'
    ];
    const csvRows = selectedRows.map((r) => [
      r.name,
      r.city,
      r.address,
      r.michelin_stars,
      r.crm_status,
      r.shelves,
      r.won_refs,
      r.to_win,
      r.last_contact_date || ''
    ]);
    const csvContent = [header, ...csvRows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      )
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `restaurant_details_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkSendToDistributor = () => {
    if (selected.size === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const selectedNames = sorted
      .filter((r) => selected.has(r.store_id))
      .map((r) => r.name);
    const notes = `Sent to distributor (bulk action): ${selectedNames.join(
      ', '
    )}`;
    Array.from(selected).forEach((id) => {
      addActivity(id, {
        type: 'sent_to_distributor',
        date: today,
        notes,
        outcome: 'pending'
      });
    });
    setSelected(new Set());
  };

  const sortArrow = (field: string) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  return (
    <ListContainer>
      <Toolbar>
        <input
          type="text"
          className="search-input"
          placeholder="Search restaurants..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {STATUS_FILTERS.map((sf) => (
          <FilterPill
            key={sf.value}
            $active={statusFilter === sf.value}
            onClick={() => setStatusFilter(sf.value)}
          >
            {sf.label}
          </FilterPill>
        ))}
      </Toolbar>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8
        }}
      >
        {/* List pills */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center'
          }}
        >
          {lists.length > 0 && (
            <>
              <span
                style={{
                  fontFamily: 'Oswald, sans-serif',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  color: '#6c757d',
                  letterSpacing: '0.5px'
                }}
              >
                Lists:
              </span>
              {lists.map((l) => (
                <FilterPill
                  key={l.id}
                  $active={filters.activeListId === l.id}
                  onClick={() =>
                    setFilters({
                      activeListId: filters.activeListId === l.id ? null : l.id
                    })
                  }
                >
                  {l.name} ({l.store_ids.length})
                </FilterPill>
              ))}
            </>
          )}
        </div>
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          {lists.length > 0 && (
            <button
              onClick={() => setShowManageLists(!showManageLists)}
              style={{
                padding: '8px 16px',
                background: 'white',
                color: '#2b3d54',
                border: '1px solid #d0d0d0',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'Oswald, sans-serif',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.5px'
              }}
            >
              My Lists
            </button>
          )}
          <button
            onClick={() => setShowUploadModal(true)}
            style={{
              padding: '8px 16px',
              background: 'white',
              color: '#e25d34',
              border: '1px solid #e25d34',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'Oswald, sans-serif',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.5px'
            }}
          >
            Upload List
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '8px 16px',
              background: '#e25d34',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'Oswald, sans-serif',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.5px'
            }}
          >
            + Add Restaurant
          </button>
        </div>
      </div>

      {showAddForm && (
        <Card>
          <div
            style={{
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }}
          >
            <div
              style={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: 14,
                textTransform: 'uppercase',
                color: '#2b3d54'
              }}
            >
              New Restaurant
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 10
              }}
            >
              <input
                placeholder="Restaurant name *"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d0d0d0',
                  borderRadius: 4,
                  fontSize: 13
                }}
              />
              <input
                placeholder="City *"
                value={newCity}
                onChange={(e) => setNewCity(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d0d0d0',
                  borderRadius: 4,
                  fontSize: 13
                }}
              />
              <input
                placeholder="Address"
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d0d0d0',
                  borderRadius: 4,
                  fontSize: 13
                }}
              />
              <input
                placeholder="Phone"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d0d0d0',
                  borderRadius: 4,
                  fontSize: 13
                }}
              />
              <input
                placeholder="Email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d0d0d0',
                  borderRadius: 4,
                  fontSize: 13
                }}
              />
              <input
                placeholder="Website"
                value={newSite}
                onChange={(e) => setNewSite(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d0d0d0',
                  borderRadius: 4,
                  fontSize: 13
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleAddRestaurant}
                style={{
                  padding: '6px 16px',
                  background: '#e25d34',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 13
                }}
              >
                Submit for Review
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                style={{
                  padding: '6px 16px',
                  background: 'white',
                  color: '#2b3d54',
                  border: '1px solid #d0d0d0',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 13
                }}
              >
                Cancel
              </button>
            </div>
            <div
              style={{ fontSize: 11, color: '#6c757d', fontStyle: 'italic' }}
            >
              The restaurant will be created with &quot;Under Review&quot;
              status while our team validates and assigns it.
            </div>
          </div>
        </Card>
      )}

      {/* Upload List Modal */}
      {showUploadModal && (
        <Card>
          <div
            style={{
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 14
            }}
          >
            <div
              style={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: 14,
                textTransform: 'uppercase',
                color: '#2b3d54'
              }}
            >
              Upload Restaurant List
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10
              }}
            >
              <input
                placeholder="List name *"
                value={uploadListName}
                onChange={(e) => setUploadListName(e.target.value)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d0d0d0',
                  borderRadius: 4,
                  fontSize: 13
                }}
              />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: '6px 14px',
                    background: 'white',
                    color: '#2b3d54',
                    border: '1px solid #d0d0d0',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 13
                  }}
                >
                  {uploadFile ? uploadFile.name : 'Choose CSV file...'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>
            </div>
            {uploadError && (
              <div style={{ color: '#dc3545', fontSize: 12 }}>
                {uploadError}
              </div>
            )}
            {uploadPreview && (
              <div
                style={{
                  overflow: 'auto',
                  border: '1px solid #e8e9eb',
                  borderRadius: 4
                }}
              >
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      {uploadPreview[0].map((h, i) => (
                        <th
                          key={i}
                          style={{
                            textAlign: 'left',
                            padding: '8px 12px',
                            fontFamily: 'Oswald, sans-serif',
                            textTransform: 'uppercase',
                            fontSize: 11,
                            fontWeight: 500,
                            color: '#6c757d',
                            borderBottom: '2px solid #e8e9eb',
                            letterSpacing: '0.3px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadPreview.slice(1).map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td
                            key={ci}
                            style={{
                              padding: '8px 12px',
                              fontSize: 13,
                              fontFamily:
                                "'SF Compact Text', -apple-system, BlinkMacSystemFont, sans-serif",
                              color: '#2b3d54',
                              borderBottom: '1px solid #f0f0f0'
                            }}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {uploadPreview.length > 6 && (
                  <div
                    style={{
                      fontSize: 11,
                      color: '#6c757d',
                      padding: '6px 12px'
                    }}
                  >
                    Showing first 5 rows...
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={handleUploadSubmit}
                disabled={!uploadFile || !uploadListName.trim()}
                style={{
                  padding: '6px 16px',
                  background:
                    !uploadFile || !uploadListName.trim() ? '#ccc' : '#e25d34',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor:
                    !uploadFile || !uploadListName.trim()
                      ? 'default'
                      : 'pointer',
                  fontSize: 13,
                  fontFamily: 'Oswald, sans-serif',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.5px'
                }}
              >
                Upload &amp; Create List
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadPreview(null);
                  setUploadError('');
                  setUploadListName('');
                }}
                style={{
                  padding: '6px 16px',
                  background: 'white',
                  color: '#e25d34',
                  border: '1px solid #e25d34',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'Oswald, sans-serif',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.5px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={downloadTemplate}
                style={{
                  padding: '6px 14px',
                  background: 'transparent',
                  color: '#6c757d',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  textDecoration: 'underline'
                }}
              >
                Download CSV Template
              </button>
            </div>
            <div
              style={{ fontSize: 11, color: '#6c757d', fontStyle: 'italic' }}
            >
              All uploaded restaurants will be created with &quot;Under
              Review&quot; status. CSV must have &quot;name&quot; and
              &quot;city&quot; columns.
            </div>
          </div>
        </Card>
      )}

      {/* Manage Lists panel */}
      {showManageLists && (
        <Card>
          <div
            style={{
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }}
          >
            <div
              style={{
                fontFamily: 'Oswald, sans-serif',
                fontSize: 14,
                textTransform: 'uppercase',
                color: '#2b3d54'
              }}
            >
              My Lists
            </div>
            {lists.length === 0 ? (
              <div style={{ fontSize: 13, color: '#6c757d' }}>
                No lists yet. Upload a CSV to create one.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {lists.map((l) => (
                  <div
                    key={l.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      background:
                        filters.activeListId === l.id ? '#fef6f3' : '#f8f9fa',
                      border: `1px solid ${
                        filters.activeListId === l.id ? '#f0d0c4' : '#e8e9eb'
                      }`,
                      borderRadius: 4
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#2b3d54',
                          fontFamily:
                            "'SF Compact Text', -apple-system, BlinkMacSystemFont, sans-serif"
                        }}
                      >
                        {l.name}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: '#6c757d',
                          fontFamily:
                            "'SF Compact Text', -apple-system, BlinkMacSystemFont, sans-serif"
                        }}
                      >
                        {l.store_ids.length} restaurants &middot; uploaded{' '}
                        {new Date(l.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete list "${l.name}"? This cannot be undone.`
                          )
                        )
                          deleteList(l.id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 4,
                        color: '#e25d34',
                        lineHeight: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title={`Delete "${l.name}"`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <button
                onClick={() => setShowManageLists(false)}
                style={{
                  padding: '6px 16px',
                  background: 'white',
                  color: '#e25d34',
                  border: '1px solid #e25d34',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'Oswald, sans-serif',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.5px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Active list indicator */}
      {activeList && (
        <div
          style={{
            padding: '8px 14px',
            background: '#fef6f3',
            border: '1px solid #f0d0c4',
            borderRadius: 4,
            fontSize: 12,
            color: '#2b3d54',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>
            Filtered by list: <strong>{activeList.name}</strong> (
            {activeList.store_ids.length} restaurants, {activeList.row_count}{' '}
            uploaded rows)
          </span>
          <button
            onClick={() => setFilters({ activeListId: null })}
            style={{
              background: 'none',
              border: 'none',
              color: '#e25d34',
              cursor: 'pointer',
              fontSize: 12,
              textDecoration: 'underline'
            }}
          >
            Clear filter
          </button>
        </div>
      )}

      {selected.size > 0 && (
        <BulkBar>
          <span>{selected.size} selected</span>
          <button onClick={handleBulkDownload}>Download Details</button>
          <button onClick={handleBulkSendToDistributor}>
            Sent to Distributor
          </button>
        </BulkBar>
      )}

      <Card>
        <TableWrapper>
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={
                      selected.size === sorted.length && sorted.length > 0
                    }
                    onChange={toggleAll}
                  />
                </th>
                <th onClick={() => toggleSort('name')}>
                  Name{sortArrow('name')}
                </th>
                <th onClick={() => toggleSort('crm_status')}>
                  Status{sortArrow('crm_status')}
                </th>
                <th className="num" onClick={() => toggleSort('shelves')}>
                  Shelves{sortArrow('shelves')}
                </th>
                <th className="num" onClick={() => toggleSort('won_refs')}>
                  Won{sortArrow('won_refs')}
                </th>
                <th className="num" onClick={() => toggleSort('to_win')}>
                  To Win Over{sortArrow('to_win')}
                </th>
                <th onClick={() => toggleSort('phone')}>
                  Phone{sortArrow('phone')}
                </th>
                <th onClick={() => toggleSort('email')}>
                  Email{sortArrow('email')}
                </th>
                <th onClick={() => toggleSort('address')}>
                  Address{sortArrow('address')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const canNavigate = r.store_id > 0;
                const navigate = () => {
                  if (canNavigate)
                    history.push(`/performance/restaurants/${r.store_id}`);
                };
                return (
                  <tr
                    key={r.store_id}
                    className={canNavigate ? 'clickable' : ''}
                  >
                    <CheckboxCell onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(r.store_id)}
                        onChange={() => toggleRow(r.store_id)}
                      />
                    </CheckboxCell>
                    <td
                      onClick={navigate}
                      style={{
                        fontWeight: 600,
                        cursor: canNavigate ? 'pointer' : 'default'
                      }}
                    >
                      {r.name}
                      {r.michelin_stars > 0 && (
                        <span
                          style={{
                            marginLeft: 6,
                            display: 'inline-flex',
                            gap: 2,
                            verticalAlign: 'middle'
                          }}
                        >
                          {Array.from({ length: r.michelin_stars }).map(
                            (_, i) => (
                              <Icon
                                key={i}
                                icon="michelinStarIcon"
                                alt="Michelin Star"
                                style={{
                                  color: '#D3072B',
                                  width: 14,
                                  height: 14
                                }}
                              />
                            )
                          )}
                        </span>
                      )}
                    </td>
                    <td onClick={navigate}>
                      <StatusBadge $status={r.crm_status}>
                        {formatStatus(r.crm_status)}
                      </StatusBadge>
                    </td>
                    <td className="num" onClick={navigate}>
                      {r.shelves}
                    </td>
                    <td className="num" onClick={navigate}>
                      {r.won_refs}
                    </td>
                    <td className="num" onClick={navigate}>
                      {r.to_win}
                    </td>
                    <td onClick={navigate}>{r.phone}</td>
                    <td onClick={navigate}>
                      {r.email ? (
                        <a
                          href={`mailto:${r.email}`}
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: '#e25d34', textDecoration: 'none' }}
                        >
                          {r.email}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td onClick={navigate}>
                      {r.address}, {r.postal_code}
                    </td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    style={{
                      textAlign: 'center',
                      color: '#6c757d',
                      padding: 30
                    }}
                  >
                    No restaurants match your search
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableWrapper>
      </Card>
    </ListContainer>
  );
};

export default RestaurantList;
